/**
 * BatchDetailsCard component for displaying Mathlete batch information
 * Based on starry-night reference implementation
 */

import * as React from 'react';

import { useIsSmallScreen } from '@/lib/hooks';
import { cn, format24to12Time, formatBatchStartDate } from '@/lib/utils';

import type { MathleteBatch } from '../../types';

export interface BatchDetailsCardProps {
  /** The batch data to display */
  batch: MathleteBatch;
  /** Click handler when batch is selected */
  onClick?: (batch: MathleteBatch) => void;
  /** Whether this batch is currently selected */
  selected?: boolean;
  /** Grid spacing for session items (unused in Tailwind version) */
  spacing?: number;
}

interface ParsedBatchData {
  formattedDate: string;
  sessions: Array<{ day: string; time: string }>;
}

/**
 * Parses batch data into display-friendly format
 */
const parseBatchData = (batch: MathleteBatch): ParsedBatchData => {
  const formattedDate = formatBatchStartDate(batch.start_date);

  const sessions = batch.course_session_info.schedule.map(session => ({
    day: session.day,
    time: format24to12Time(session.time),
  }));

  return {
    formattedDate,
    sessions,
  };
};

/**
 * Display card for a Mathlete batch with date and schedule information
 *
 * Shows batch start date and session schedule. Supports desktop and mobile layouts
 * with visual indication of selection state.
 *
 * @example
 * ```tsx
 * <BatchDetailsCard
 *   batch={batchData}
 *   selected={selectedBatch?.batch_id === batchData.batch_id}
 *   onClick={handleBatchClick}
 * />
 * ```
 */
export const BatchDetailsCard: React.FC<BatchDetailsCardProps> = ({
  batch,
  onClick,
  selected = false,
}) => {
  const isMobile = useIsSmallScreen();
  const parsedData = parseBatchData(batch);
  const handleClick = () => {
    onClick?.(batch);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const containerClasses = cn(
    'rounded-lg p-3 m-3 cursor-pointer transition-all',
    selected ? 'border-[3px] border-bhanzu-orange' : 'border border-gray-400'
  );

  // Desktop Layout
  if (!isMobile) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={containerClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <p className="text-xs text-gray-600">Starts from</p>
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {parsedData.formattedDate}
        </p>

        {/* Dashed divider */}
        <div className="border-b border-dashed border-gray-300 my-2" />

        <p className="text-xs text-gray-600 my-2">Sessions will be on every</p>

        {/* Session grid */}
        <div className="grid grid-cols-3 gap-0">
          {parsedData.sessions.map((session, index) => (
            <div key={`${session.day}-${index}`} className="text-center">
              <p className="text-sm text-gray-900">{session.day}</p>
              <p className="text-xs text-gray-600">{session.time}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div
      role="button"
      tabIndex={0}
      className={containerClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex">
        {/* Left section - Start date */}
        <div className="flex flex-col justify-center w-[27%] border-r border-dashed border-gray-300 pr-1">
          <p className="text-xs text-gray-600">Starts from</p>
          <p className="text-sm font-semibold text-gray-900">
            {parsedData.formattedDate}
          </p>
        </div>

        {/* Right section - Sessions */}
        <div className="flex flex-col justify-center w-[68%] ml-2">
          <p className="text-xs text-gray-600 my-1">
            Sessions will be on every
          </p>
          <div className="grid grid-cols-3 gap-0">
            {parsedData.sessions.map((session, index) => (
              <div key={`${session.day}-${index}`} className="text-center">
                <p className="text-sm text-gray-900">{session.day}</p>
                <p className="text-xs text-gray-600">{session.time}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
